// src/main.jsx
// Boot order matters:
//   1. Sentry FIRST — catches errors during all subsequent init steps
//   2. Service worker + IDB
//   3. Background sync engine
//   4. Stale cache revalidation
//   5. React

import React from 'react';
import { initAppCheck } from '@services/appCheck';
import { initFirebase } from '@services/storage/firebase';
Promise.race([initAppCheck(), new Promise(r => setTimeout(r, 3000))]).then(() => initFirebase()).then(({ auth }) => {
  // Handle Google sign-in redirect result immediately after Firebase is ready
  import('firebase/auth').then(({ getRedirectResult }) => {
    getRedirectResult(auth).then(result => {
      if (result?.user) {
        console.log('[main] Google redirect complete:', result.user.email, result.user.uid);
        window.__googleRedirectUser = result.user;
      }
    }).catch(() => {});
  });
}).catch(e => console.error('[main] Firebase init failed:', e));
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AppErrorBoundary } from '@shell/ErrorBoundary';
import { initSentry, breadcrumb, breadcrumbOffline } from '@services/monitoring';
import { initOffline }       from '@services';
import { startSyncEngine }   from '@services/sync/engine.js';
import { StaleCacheManager } from '@services/sync/staleCache.js';
import App from './App';

(async () => {
  // ── 1. Sentry (must be before everything else) ─────────────────
  await initSentry();

  // ── 2. Service worker ──────────────────────────────────────────
  // Register first — before Firebase, before React. The SW installs
  // the offline shell and starts caching tiles immediately. If we
  // wait until after React mounts, the first load isn't cached.
  //
  // updateViaCache: 'none' — always re-fetch sw.js from network.
  // The SW itself controls cache headers for all other assets.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        breadcrumb('SW registered, scope: ' + reg.scope);
        // Trigger update check on each boot so users don't run stale
        // code indefinitely. The UpdateBanner handles the UI prompt.
        reg.update().catch(() => {});
      })
      .catch((err) => {
        // SW registration failure is non-fatal — app works without it,
        // just no offline caching or background push.
        console.warn('[SW] Registration failed:', err.message);
      });
  }

  // ── 3. Offline infrastructure ──────────────────────────────────
  await initOffline();

  // ── 4. Background sync engine ──────────────────────────────────
  await startSyncEngine();

  // ── 5. Stale cache revalidation ────────────────────────────────
  StaleCacheManager.revalidateAll().catch(() => {});

  // ── 6. Connectivity → offlineStore + Sentry breadcrumbs ─────────
  const { useOfflineStore: _offlineStore } = await import('@store/offlineStore');
  const _setOnline = _offlineStore.getState().setOnline;
  window.addEventListener('online',  () => { _setOnline(true);  breadcrumbOffline(true);  });
  window.addEventListener('offline', () => { _setOnline(false); breadcrumbOffline(false); });
  // Initialize with current state
  _setOnline(navigator.onLine);

  // ── 7. Mount React ─────────────────────────────────────────────
  breadcrumb('React mounting');
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AppErrorBoundary>
    </React.StrictMode>
  );
})();
